<script setup>
import { computed, onMounted, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useOpportunitiesStore } from 'dashboard/stores/opportunities';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const STAGES = [
  'prospecting',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
];

const opportunitiesStore = useOpportunitiesStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const opportunityId = computed(() => route.params.opportunityId);
const opportunity = computed(() =>
  opportunitiesStore.getRecord(opportunityId.value)
);
const uiFlags = computed(() => opportunitiesStore.getUIFlags);
const isFetching = computed(() => uiFlags.value.fetchingItem);
const isUpdating = computed(() => uiFlags.value.updatingItem);
const isDeleting = computed(() => uiFlags.value.deletingItem);

const stageOptions = STAGES.map(stage => ({
  value: stage,
  label: t(`OPPORTUNITIES.STAGES.${stage.toUpperCase()}`),
}));

const form = reactive({
  name: '',
  value: '',
  currency: '',
  stage: 'prospecting',
  manualProbability: '',
  expectedCloseDate: '',
  lostReason: '',
});

const syncFormWithOpportunity = record => {
  if (!record?.id) return;
  form.name = record.name || '';
  form.value = record.value || '';
  form.currency = record.currency || '';
  form.stage = record.stage || 'prospecting';
  form.manualProbability = record.manualProbability ?? '';
  form.expectedCloseDate = record.expectedCloseDate || '';
  form.lostReason = record.lostReason || '';
};

watch(opportunity, syncFormWithOpportunity);

const isFormInvalid = computed(
  () => !form.name.trim() || (form.stage === 'lost' && !form.lostReason.trim())
);

const backToList = () => {
  router.push({
    name: 'opportunities_dashboard_index',
    params: { accountId: route.params.accountId },
  });
};

const save = async () => {
  if (isFormInvalid.value) return;
  try {
    await opportunitiesStore.update({
      id: opportunityId.value,
      name: form.name.trim(),
      value: form.value || null,
      currency: form.currency.trim() || null,
      stage: form.stage,
      manualProbability: form.manualProbability || null,
      expectedCloseDate: form.expectedCloseDate || null,
      lostReason: form.lostReason.trim() || null,
    });
    useAlert(t('OPPORTUNITIES.DETAIL.MESSAGES.UPDATE_SUCCESS'));
  } catch {
    useAlert(t('OPPORTUNITIES.DETAIL.MESSAGES.UPDATE_ERROR'));
  }
};

const remove = async () => {
  try {
    await opportunitiesStore.delete(opportunityId.value);
    useAlert(t('OPPORTUNITIES.DETAIL.MESSAGES.DELETE_SUCCESS'));
    backToList();
  } catch {
    useAlert(t('OPPORTUNITIES.DETAIL.MESSAGES.DELETE_ERROR'));
  }
};

onMounted(async () => {
  const record = await opportunitiesStore.show(opportunityId.value);
  syncFormWithOpportunity(record);
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <header class="flex items-center gap-3 px-6 py-4 border-b border-n-weak">
      <Button
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        @click="backToList"
      />
      <span class="text-lg font-semibold text-n-slate-12">
        {{ t('OPPORTUNITIES.DETAIL.TITLE') }}
      </span>
    </header>

    <div v-if="isFetching" class="flex items-center justify-center flex-1">
      <span class="text-sm text-n-slate-11">
        {{ t('OPPORTUNITIES.DETAIL.LOADING') }}
      </span>
    </div>

    <div v-else class="flex flex-col gap-6 px-6 py-6 max-w-2xl">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-model="form.name"
          :label="t('OPPORTUNITIES.FIELDS.NAME')"
          :disabled="isUpdating"
        />
        <Select
          v-model="form.stage"
          :options="stageOptions"
          :placeholder="t('OPPORTUNITIES.FIELDS.STAGE')"
          :disabled="isUpdating"
        />
        <Input
          v-model="form.value"
          type="number"
          :label="t('OPPORTUNITIES.FIELDS.VALUE')"
          :disabled="isUpdating"
        />
        <Input
          v-model="form.currency"
          :label="t('OPPORTUNITIES.FIELDS.CURRENCY')"
          :disabled="isUpdating"
        />
        <Input
          v-model="form.manualProbability"
          type="number"
          :label="t('OPPORTUNITIES.FIELDS.MANUAL_PROBABILITY')"
          :disabled="isUpdating"
        />
        <input
          v-model="form.expectedCloseDate"
          type="date"
          :disabled="isUpdating"
          class="w-full h-8 px-3 mt-5 text-sm bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12"
        />
      </div>

      <TextArea
        v-if="form.stage === 'lost'"
        v-model="form.lostReason"
        :placeholder="t('OPPORTUNITIES.FIELDS.LOST_REASON')"
        :disabled="isUpdating"
        class="w-full"
      />

      <div class="flex items-center justify-between">
        <Button
          :label="t('OPPORTUNITIES.DETAIL.ACTIONS.DELETE')"
          variant="ghost"
          color="ruby"
          :is-loading="isDeleting"
          @click="remove"
        />
        <Button
          :label="t('OPPORTUNITIES.DETAIL.ACTIONS.SAVE')"
          color="blue"
          :disabled="isFormInvalid || isUpdating"
          :is-loading="isUpdating"
          @click="save"
        />
      </div>
    </div>
  </div>
</template>
